import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Layout } from './components/Layout';
import { Programs } from './pages/Programs';
import ProgramDetails from './components/ProgramDetails';
import { Students } from './pages/Students';
import { InvoiceDetails } from './pages/InvoiceDetails';
import { Payments } from './pages/Payments';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import AccessDenied from './pages/AccessDenied';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandProfileProvider } from './contexts/BrandProfileContext';
import { FeatureFlagOverrideProvider } from './contexts/FeatureFlagOverrideContext';
import ParentOnboardingCheck from './components/ParentOnboardingCheck';
import { ParentOnboarding } from './components/ParentOnboarding';
import { PaymentCheckout } from './pages/PaymentCheckout';
import { CartCheckout } from './pages/CartCheckout';
import { CartConfirmation } from './pages/CartConfirmation';
import { PaymentConfirmation } from './pages/PaymentConfirmation';
import { AdminApp } from './admin-app/AdminApp';
import { AdminCallback } from './admin-app/AdminCallback';
import { StaffApp } from './staff-app/StaffApp';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import FAQ from './pages/FAQ';
import ParentGenie from './pages/ParentGenie';
import HomePage from './pages/HomePage';
import EnrollmentDetails from './pages/EnrollmentDetails';
import ResetPassword from './pages/ResetPassword';
import { Account } from './pages/Account';
import AddPaymentMethod from './pages/AddPaymentMethod';
import { Coupons } from './pages/Coupons';
import { Wallet } from './pages/Wallet';
import { WalletHistory } from './pages/WalletHistory';
import AuthenticatedSignupPage from './pages/AuthenticatedSignupPage';
import SignupEvents from './pages/SignupEvents';
import StudentWaiver from './pages/StudentWaiver';
import EnrollmentHistory from './pages/EnrollmentHistory';
import AcceptCoParentInvite from './pages/AcceptCoParentInvite';
import Pickup from './pages/Pickup';
import { useClosures } from './hooks/useClosures';
import { BackendOutageScreen } from './components/BackendOutageScreen';

const queryClient = new QueryClient();

function AuthenticatedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    // Preserve the intended destination in URL query parameters
    // This ensures it survives the OAuth redirect
    const searchParams = new URLSearchParams();
    searchParams.set('redirectTo', location.pathname + location.search);
    
    return <Navigate to={`/login?${searchParams.toString()}`} replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, isAdmin, isAdminLoading, isStaff, isStaffLoading } = useAuth();

  if (isLoading || (user && (isAdminLoading || isStaffLoading))) {
    return <div>Loading...</div>;
  }

  if (user) {
    if (isAdmin) return <Navigate to="/admin-app" replace />;
    if (isStaff) return <Navigate to="/staff-app" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  // Loads the closure calendar once for every shell below — parent, admin and staff all
  // route from here. Nothing waits on it: the synchronous accessors in
  // `config/blackoutDates` answer from the compiled-in fallback until it resolves, so a slow
  // or failed load degrades to last-deploy's calendar rather than to an empty one.
  useClosures();

  return (
    <Routes>
      <Route path="/admin-app/*" element={<AdminApp />} />
      <Route path="/admin-callback" element={<AdminCallback />} />
      <Route path="/staff-app/*" element={<StaffApp />} />
      <Route path="*" element={
        <AuthProvider>
          {/* Inside AuthProvider (it reads the account to scope the stored profile) and inside
              the router (it consumes `?brand=`). Admin and teacher shells route above this and
              are deliberately untouched — brand is a parent-facing view, and the admin portal
              already sees every brand at once. */}
          <BrandProfileProvider>
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={
              <AuthenticatedRoute>
                <ParentOnboarding />
              </AuthenticatedRoute>
            } />
            {/* Full screen, outside the parent shell: a parent at the door gets their children and
                one button, with no menu or assistant bubble to tap by mistake. */}
            <Route path="/pickup" element={
              <AuthenticatedRoute>
                <Pickup />
              </AuthenticatedRoute>
            } />
            <Route element={
              <AuthenticatedRoute>
                <Layout />
              </AuthenticatedRoute>
            }>
              <Route path="/dashboard" element={<ParentOnboardingCheck />} />
              <Route path="/programs" element={<Programs />} />
              <Route path="/programs/:id" element={<ProgramDetails />} />
              <Route path="/students" element={<Students />} />
              <Route path="/invoices/:id" element={<InvoiceDetails />} />
              <Route path="/payments" element={<Payments />} />
              <Route path="/enrollments/:enrollment_id" element={<EnrollmentDetails />} />
              <Route path="/enrollment-history" element={<EnrollmentHistory />} />
              <Route path="/payment-checkout/:invoiceId" element={<PaymentCheckout />} />
              <Route path="/cart" element={<CartCheckout />} />
              <Route path="/cart/:cartId" element={<CartConfirmation />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/assistant" element={<ParentGenie />} />
              <Route path="/account" element={<Account />} />
              <Route path="/account/add-payment-method" element={<AddPaymentMethod />} />
              <Route path="/wallet" element={<Wallet />} />
              <Route path="/wallet/history" element={<WalletHistory />} />
              <Route path="/coupons" element={<Coupons />} />
              <Route path="/signup" element={<SignupEvents />} />
              <Route path="/signup/:eventId" element={<AuthenticatedSignupPage />} />
              <Route path="/student-waiver" element={<StudentWaiver />} />
              <Route path="/accept-invite" element={<AcceptCoParentInvite />} />
            </Route>
            <Route path="/payment-confirmation" element={
              <AuthenticatedRoute>
                <PaymentConfirmation />
              </AuthenticatedRoute>
            } />
            <Route path="/access-denied" element={<AccessDenied />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
            {/* The link the Kaleido site and flyers give out. A signed-out parent is sent to
                sign in and comes back here with the brand intact (see BrandProfileProvider). */}
            <Route path="/kaleido" element={<Navigate to="/dashboard?brand=kaleido" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </BrandProfileProvider>
        </AuthProvider>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagOverrideProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
          <Analytics />
          <SpeedInsights />
        </BrowserRouter>
        <BackendOutageScreen />
      </FeatureFlagOverrideProvider>
    </QueryClientProvider>
  );
}

export function AppWithRouter() {
  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagOverrideProvider>
        <AppRoutes />
      </FeatureFlagOverrideProvider>
    </QueryClientProvider>
  );
}